import React, { useMemo } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';

import { observer } from 'mobx-react';
import { Route, Router, Switch, useParams } from 'react-router-dom';

import { FormSubmitEvent } from 'ui/base/types';
import { useStyles } from 'ui/base/theme';
import { BusyStore } from 'ui/base/button';
import { PasswordStore } from 'ui/base/input';
import { HistoryModalStore, Modal } from 'ui/base/modal';
import { StandardPage } from 'ui/base/page/standard_page';
import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';
import { Step, StepContent, StepLabel, Stepper } from 'ui/standard/stepper/stepper';
import { ActiveButton, DefaultButton } from 'ui/public/button';
import { LabeledInput, PasswordInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { ResultKind } from 'services/base/types';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';
import { bytesToSize } from 'services/base/bytes';
import { Pangolin } from 'services/accounts/types';
import { IPangolinDomain, UpdateRelease, UpdateSnapshot } from 'services/pangolin/types';
import { UpdateRestoreService } from 'services/pangolin/update_restore_service';

import { themedStyles } from '../manage_updates';
import { UpdateRestoreStepper } from '../ui/update_restore_stepper';

import Messages from './update_restore_modal_messages';

export type UpdateRestoreVersionModalProps = UpdateRestoreModalProps;
export const UpdateRestoreVersionModal = observer(({ modal, domain }: UpdateRestoreVersionModalProps) => {
  const { id } = useParams<{ id: string }>();
  const styles = useStyles(themedStyles);

  const release = domain.updateReleases.get(id);

  return (
    <div className={classNames(styles.modalContainerBig, { [styles.modalContainerPopOut]: modal.visible })}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.versionInfoTitle()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <div className={styles.field}>
              <div className={styles.fieldTextLeft} >
                {release?.tag}
                <div className={styles.fieldInfo}>
                  {Messages.versionInfoFieldVersion()}
                </div>
              </div>
              <img className={styles.fieldIcon}
                  src={styles.fieldIconImageVersion}
                  alt='Version Icon' />
            </div>
            {release?.notes && (
              <div className={styles.row}>
                <div className={classNames(styles.column, styles.corpus)}>
                  <div className={styles.label}>
                    {Messages.versionInfoFieldNotes()}
                  </div>
                  <div className={styles.value}>
                    <ReactMarkdown>{release.notes}</ReactMarkdown>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.section}>
            <DefaultButton type='button' onClick={modal.history.goBack}>
              {Messages.versionInfoButtonBack()}
            </DefaultButton>
          </div>
        </div>
      </StandardPage>
    </div>
  );
});

export type UpdateRestoreConfirmModalProps = UpdateRestoreModalProps;
export const UpdateRestoreConfirmModal = observer(({ root, pangolin, domain, modal }: UpdateRestoreConfirmModalProps) => {
  const { id: snapshotId } = useParams<{ id: string }>();

  const styles = useStyles(themedStyles);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  const sourceId = pangolin.updateActiveReleaseID;
  const sourceRelease = sourceId ? domain.updateReleases.get(sourceId) : undefined;
  const targetSnapshot = snapshotId ? domain.updateSnapshots.get(snapshotId) : undefined;
  const targetRelease = targetSnapshot ? domain.updateReleases.get(targetSnapshot.release_id) : undefined;

  const [busy] = React.useState(new BusyStore());
  const [passwordStore] = React.useState(new PasswordStore(''));
  const [passwordError, setPasswordError] = React.useState(' ');

  const disabled = passwordStore.value.length === 0;

  const onSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();

    setPasswordError(' ');

    if (!targetSnapshot) {
      setPasswordError(Messages.errorGeneric());
      return;
    }

    const result = await busy.perform(async() => {
      try {
        modal.freezeModal();
        return await UpdateRestoreService.dispatch(
          domain, pangolin, targetSnapshot,
          passwordStore.value
        );
      } finally {
        modal.unfreezeModal();
      }
    });

    if (result.kind === ResultKind.OK) {
      modal.dismissModal();
    } else if (result.status === 401) {
      // Display authorization error
      setPasswordError(Messages.errorUnauthorized());
    } else if (result.status === 402) {
      // Display plan required error
      setPasswordError(Messages.errorPlanRequired());
    } else {
      // Display generic error
      setPasswordError(Messages.errorGeneric());
    }
  }, [modal]);

  const onShowSourceModal = React.useCallback(() => {
    if (sourceRelease) modal.history.push(`${root}/info/releases/${sourceRelease.id}`);
  }, [modal, root, sourceRelease]);

  const onShowTargetModal = React.useCallback(() => {
    if (targetRelease) modal.history.push(`${root}/info/releases/${targetRelease.id}`);
  }, [modal, root, targetRelease]);

  return (
    <div className={classNames(styles.modalContainerLargest, { [styles.modalContainerPopOut]: modal.visible })}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.confirmRestoreTitle()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <form className={styles.modalContentRow} onSubmit={onSubmit}>
          <div className={styles.section}>
            <div className={classNames(styles.field, styles.item)}>
              <div className={styles.fieldTextLeft}>
                {pangolin.nickname}
                <div className={styles.fieldInfo}>
                  {Messages.confirmRestoreFieldPangolin()}
                </div>
              </div>
              <ShieldIcon
                className={styles.fieldIcon}
                colorCode={pangolin.colorCode}
                size={ShieldSize.SMALL}
              />
              <div className={classNames(styles.open, styles.disabled)} />
            </div>
            {sourceRelease && (
              <div className={classNames(styles.field, styles.item, { [styles.clickable]: sourceRelease })} onClick={onShowSourceModal}>
                <div className={styles.fieldTextLeft}>
                  {sourceRelease.tag}
                  <div className={styles.fieldInfo}>
                    {Messages.confirmRestoreFieldSource()}
                  </div>
                </div>
                <img className={styles.fieldIcon} alt='Version Icon'
                    src={styles.fieldIconImageSource} />
                <div className={classNames(styles.open, { [styles.disabled]: !sourceRelease })} />
              </div>
            )}
            <div className={classNames(styles.field, styles.item, { [styles.clickable]: targetRelease })} onClick={onShowTargetModal}>
              <div className={styles.fieldTextLeft}>
                {targetRelease?.tag}
                <div className={styles.fieldInfo}>
                  {Messages.confirmRestoreFieldTarget()}
                </div>
              </div>
              <img className={styles.fieldIcon} alt='Version Icon'
                   src={styles.fieldIconImageTarget} />
              <div className={classNames(styles.open, { [styles.disabled]: !targetRelease })} />
            </div>
            <div className={classNames(styles.field, styles.item)}>
              <div className={styles.fieldTextLeft}>
                {targetSnapshot?.created_at.toLocaleString(locale, format)}
                <div className={styles.fieldInfo}>
                  {Messages.confirmRestoreFieldDate()}
                </div>
              </div>
              <img className={styles.fieldIcon} alt='Modified Icon'
                   src={styles.fieldIconImageModified} />
              <div className={classNames(styles.open, styles.disabled)} />
            </div>
          </div>
          <div className={styles.section}>
            <UpdateRestoreStepper />
            <div className={styles.sectionTitle}>
              {Messages.confirmRestoreFieldNotes()}
            </div>
            <ul className={styles.notes}>
              <li className={styles.item}>
                {Messages.confirmRestoreNoteReboot()}
              </li>
            </ul>
            <LabeledInput label={Messages.confirmRestoreInputPassword()}>
              <PasswordInput
                store={passwordStore}
                name='password'
                id='password'
              />
            </LabeledInput>
            <ErrorMessage message={passwordError}/>
            <ActiveButton busy={busy} disabled={disabled}>
              {Messages.confirmRestoreButtonRestore()}
            </ActiveButton>
            <DefaultButton type='button' disabled={busy.value} onClick={modal.history.goBack}>
              {Messages.confirmRestoreButtonBack()}
            </DefaultButton>
          </div>
        </form>
      </StandardPage>
    </div>
  );
});

export type UpdateRestoreItemProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  snapshot: UpdateSnapshot;
  release: UpdateRelease;
  modal: HistoryModalStore;
  root: string;
};
export const UpdateRestoreItem = observer(({
  pangolin,
  domain,
  snapshot,
  release,
  modal,
  root,
}: UpdateRestoreItemProps) => {
  const styles = useStyles(themedStyles);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  const version = pangolin.updateVersion;

  const current = (
    !pangolin.updateActiveReleaseID ? undefined :
    domain.updateReleases.get(pangolin.updateActiveReleaseID)
  );

  const position = useMemo(() => {
    const release_tag = release.tag;
    const current_tag = current?.tag ?? version;
    if (!current_tag) return 'future';
    const release_tag_int = parseInt(release_tag);
    const current_tag_int = parseInt(current_tag);
    if (release_tag_int > current_tag_int) return 'future';
    if (release_tag_int < current_tag_int) return 'previous';
    if (release_tag_int === current_tag_int) return 'current';
    return 'future';
  }, [current, version]);

  const stepStatus = (
    position === 'future' ? 'pending' :
    position === 'previous' ? 'pending' :
    'active'
  );

  const stepClassName = classNames({
    [styles.clickable]: position !== 'current',
  });

  const openClassName = classNames(styles.open, {
    [styles.disabled]: position === 'current',
  });

  const statusLabel = (
    position === 'future' ? Messages.snapshotListStatusAvailable() :
    position === 'previous' ? Messages.snapshotListStatusAvailable() :
    Messages.snapshotListStatusCurrent()
  );

  const onClick = position !== 'current' ? React.useCallback(() => {
    modal.history.push(`${root}/snapshots/${snapshot.id}`);
  }, [modal, root, snapshot]) : undefined;

  return (
    <Step status={stepStatus} className={stepClassName} onClick={onClick}>
      <StepLabel>
        <div className={styles.field}>
          <div className={styles.fieldTextRight}>
            {snapshot.created_at.toLocaleString(locale, format)}
            <div className={styles.fieldInfo}>
              {statusLabel}
            </div>
          </div>
          <div className={openClassName}/>
        </div>
      </StepLabel>
      <StepContent>
        <div className={styles.field}>
          <div className={styles.fieldCorpusRight}>
            {release.tag}{snapshot.size ? ` (${bytesToSize(snapshot.size)})` : ''}
          </div>
          <div className={styles.pad}/>
        </div>
      </StepContent>
    </Step>
  );
});

export type UpdateRestoreListModalProps = UpdateRestoreModalProps;
export const UpdateRestoreListModal = observer(({
  pangolin,
  domain,
  modal,
  root,
}: UpdateRestoreListModalProps) => {
  const styles = useStyles(themedStyles);
  const snapshots = domain.updateSnapshots.values.map(snapshot => (
    [snapshot, domain.updateReleases.get(snapshot.release_id)]
  )).filter(([_, release]) => !!release) as [UpdateSnapshot, UpdateRelease][];
  return (
    <div className={classNames(styles.modalContainerBig, { [styles.modalContainerPopOut]: modal.visible })}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.snapshotListTitle()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <Stepper>
            {snapshots.map(([snapshot, release]) => (
              <UpdateRestoreItem
                key={snapshot.id}
                pangolin={pangolin}
                domain={domain}
                snapshot={snapshot}
                release={release}
                modal={modal}
                root={root}
              />
            ))}
          </Stepper>
        </div>
      </StandardPage>
    </div>
  );
});

export type UpdateRestoreModalProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  version: string | null;
  modal: HistoryModalStore;
  root: string;
};
export const UpdateRestoreModal = observer((props: UpdateRestoreModalProps) => {
  const { modal, root } = props;
  return (
    <Modal store={modal} onBackdropClick={modal.history.goBack}>
      <Router history={modal.history}>
        <Switch>
          <Route exact path={`${root}/snapshots`}>
            <UpdateRestoreListModal {...props} />
          </Route>
          <Route exact path={`${root}/snapshots/:id`}>
            <UpdateRestoreConfirmModal {...props} />
          </Route>
          <Route exact path={`${root}/info/releases/:id`}>
            <UpdateRestoreVersionModal {...props} />
          </Route>
        </Switch>
      </Router>
    </Modal>
  );
});
